import React, { useState, useEffect, useRef, useContext } from "react";
import { LocalizationContext } from "../contexts/localization-context";
import { ArtsContext } from "../contexts/arts-context";
import ArtVoteButton from "./ArtVoteButon";
import Swiper from "swiper/dist/js/swiper.min";
import ArtistInfoOverlay from "./ArtistInfoOverlay";

const ArtOverlayMobile = ({ activeArt, setActiveArt, votedArtId, setVotedArtId, campaignName }) => {
    const [localizationState, setLocalizationState] = useContext(LocalizationContext);
    const [arts, dispatchToArts] = useContext(ArtsContext);

    const [scrollY, setScrollY] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [infoIsVisible, setInfoIsVisible] = useState(false);

    const swiperContainerRef = useRef(null);
    const swiperPrevRef = useRef(null);
    const swiperNextRef = useRef(null);
    const swiperPaginationRef = useRef(null);
    const artistInfoHeaderRef = useRef(null);
    const artistInfoHeaderButtonRef = useRef(null);

    const activeArtIndex =
        arts && activeArt ? arts.findIndex((art) => art.nodeId === activeArt.nodeId) : null;

    let swiper;

    const initSwiper = () => {
        swiper = new Swiper(swiperContainerRef.current, {
            initialSlide: activeArtIndex,
            speed: 600,
            slidesPerView: 1,
            spaceBetween: 24,
            navigation: {
                nextEl: swiperNextRef.current,
                prevEl: swiperPrevRef.current,
            },
            pagination: {
                el: swiperPaginationRef.current,
                type: "fraction",
            },
            a11y: true,
            a11y: {
                prevSlideMessage: "Previous slide",
                nextSlideMessage: "Next slide",
                firstSlideMessage: "This is the first slide",
                lastSlideMessage: "This is the last slide",
                paginationBulletMessage: "Go to slide {{index}}",
            },
            on: {
                slideChange: function () {
                    setActiveArt(arts[swiper.activeIndex]);
                },
            },
        });
    };

    const lockScrollPosition = () => {
        let scrollPosition = window.scrollY || document.documentElement.scrollTop;
        setScrollY(scrollPosition);
        document.querySelector("body").classList.add("no-scroll-desktop");
        document.querySelector("body").style.top = `-${scrollPosition}px`;
    };

    const unlockScrollPosition = () => {
        document.querySelector("body").classList.remove("no-scroll-desktop");
        setTimeout(function () {
            document.querySelector("body").style.top = "auto";
            document.documentElement.scrollTop = scrollY;
        }, 0);
    };

    useEffect(() => {
        initSwiper();
        setIsVisible(true);
        lockScrollPosition();

        return () => {
            swiper.destroy();
        };
    }, []);

    const closeOverlay = () => {
        setIsVisible(false);
        unlockScrollPosition();

        setTimeout(() => {
            setActiveArt(null);
        }, 300);
    };

    return (
        <div
            className={`art-overlay z-100 bg-true-white fixed top-0 left-0 h-full w-full opacity-0 fade${
                isVisible ? " fade-in" : ""
            }`}
        >
            <button className="fixed p-10 right-6 top-0 cursor-pointer z-40" onClick={closeOverlay}>
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13">
                    <path
                        d="M11.843 10.556 7.601 6.314l4.242-4.243A1 1 0 1 0 10.43.657L6.186 4.899 1.944.657A1 1 0 1 0 .529 2.07l4.243 4.243L.53 10.556a1 1 0 1 0 1.415 1.415l4.242-4.243 4.243 4.243a1 1 0 1 0 1.414-1.415"
                        fill="#000"
                    />
                </svg>
            </button>
            <div
                className="w-full relative bg-gray-brown-x-light pt-40 pb-24 px-16 flex justify-between items-center"
                ref={artistInfoHeaderRef}
            >
                <div className="flex justify-center items-center">
                    <img
                        className="w-48 h-48 rounded-full box-content mr-10"
                        src={activeArt.authorImage}
                        alt={activeArt.authorName}
                    />
                    <div>
                        <p className="text-14 text-gray-brown-dark leading-none">
                            {activeArt.authorName}
                        </p>
                        <button
                            className="text-14 text-primary leading-none flex items-center mt-4 focus:outline-none"
                            ref={artistInfoHeaderButtonRef}
                            onClick={() => setInfoIsVisible(true)}
                        >
                            Details
                            <span className="arrow-img"></span>
                        </button>
                    </div>
                </div>
                <div className="flex flex-col justify-center items-center">
                    <span className="font-bold text-primary text-24 3xs:text-16 leading-2xs">
                        {activeArt.votes}
                    </span>
                    <span className="mt-4 3xs:mt-0 text-14 3xs:text-12">
                        {localizationState.votes}
                    </span>
                </div>
            </div>
            {infoIsVisible && (
                <ArtistInfoOverlay
                    activeArt={activeArt}
                    setInfoIsVisible={setInfoIsVisible}
                    artistInfoHeaderRef={artistInfoHeaderRef}
                    artistInfoHeaderButtonRef={artistInfoHeaderButtonRef}
                />
            )}
            <div className="w-full p-24 3xs:px-0 h-screen-210 overflow-hidden">
                <div className="swiper-container" ref={swiperContainerRef}>
                    <div className="swiper-wrapper">
                        {arts.map((art) => (
                            <div key={art.nodeId} className="swiper-slide">
                                <img
                                    className="h-auto max-h-full centered relative"
                                    src={art.image}
                                    alt={art.authorName}
                                />
                            </div>
                        ))}
                    </div>
                    <div
                        className="absolute centered-y left-8 h-40 w-40 bg-black-o-80 z-10 rounded-full flex items-center justify-center border-2 border-white-o-50"
                        ref={swiperPrevRef}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="16"
                            viewBox="0 0 8 12"
                        >
                            <path d="m6 0 1.4 1.4L2.8 6l4.6 4.6L6 12 0 6z" fill="#fcfcfc" />
                        </svg>
                    </div>
                    <div
                        className="absolute centered-y right-8 h-40 w-40 bg-black-o-80 z-10 rounded-full flex items-center justify-center border-2 border-white-o-50"
                        ref={swiperNextRef}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="16"
                            viewBox="0 0 8 12"
                        >
                            <path
                                d="M1.833 0 .434 1.4l4.6 4.6-4.6 4.6 1.4 1.4 6-6z"
                                fill="#fcfcfc"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            <ArtVoteButton
                className="text-center"
                artId={activeArt.nodeId}
                votedArtId={votedArtId}
                setVotedArtId={setVotedArtId}
                campaignName={campaignName}
            />

            <div className="text-center text-14 mt-24" ref={swiperPaginationRef}></div>
        </div>
    );
};

export default ArtOverlayMobile;
